import { FaTrash } from "react-icons/fa";
import { useState } from "react";
import { FiDatabase, FiCloud, FiCpu } from "react-icons/fi";
import {
  isTrackerStale,
  isTrackerActive,
  renderProgressCell,
  renderFilesCell,
  renderSuccessRateCell,
  renderErrorRateCell,
  renderSchemaNameCell,
  renderTimelineCell,
  TagsCell,
  renderErrorDataTableCell,
  renderProcessingTimeCell,
  renderOnlyDedupeCell,
  ChunkMappingCell,
  renderTagDetailsCell,
  AdvancedSettingsCell,
  HierarchyCell,
} from "./ProgressRowRenderUtils";
import { handleAbort } from "./utils";

export const ProgressTrackerRow = ({
  tracker,
  handleDelete,
  handleCheckboxClick,
  selectedTrackers,
  setSelectedTrackers,
  deasyApiKey,
  setProgressTrackers,
  isPending = false,
}) => {
  const isStale = isTrackerStale(tracker);
  const isActive = isTrackerActive(tracker);
  const [abortingTracker, setAbortingTracker] = useState(false);
  const [deletingTracker, setDeletingTracker] = useState(false);
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(true);

  // Files state
  const [filesSearchQuery, setFilesSearchQuery] = useState("");
  const [filesPage, setFilesPage] = useState(1);
  const [filesPerPage, setFilesPerPage] = useState(10);
  const [expanded, setExpanded] = useState(false);

  // Row actions
  const handleRowClick = (trackerId) => {
    setExpanded((prev) => !prev);
    setSelectedTrackers((prev) =>
      prev.includes(trackerId)
        ? prev.filter((id) => id !== trackerId)
        : [...prev, trackerId],
    );
  };

  return (
    <>
      <tr
        key={tracker.tracker_id}
        className={`${
          selectedTrackers.includes(tracker.tracker_id)
            ? "bg-primary/15"
            : "hover:bg-gray-50"
        } ${tracker.status === "completed" ? "h-12" : ""} cursor-pointer ${
          isPending ? "bg-yellow-50" : ""
        }`}
        onClick={() => handleRowClick(tracker.tracker_id)}
      >
        <td
          className="pl-6 pr-2 text-left"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="rounded border-gray-300 text-primary focus:ring-primary"
            checked={selectedTrackers.includes(tracker.tracker_id)}
            onChange={(e) => handleCheckboxClick(e, tracker.tracker_id)}
          />
        </td>
        <td className="pr-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-left">
          <span className="font-semibold rounded-md px-2 py-1 bg-gray-100 shadow-md border border-gray-200">
            {tracker.task_type}
          </span>
        </td>
        {renderProgressCell(tracker)}
        {renderFilesCell(tracker)}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-left">
          <div className="flex items-center">
            <div className="text-sm font-medium text-gray-900">
              {tracker.is_pending ? (
                <span className="text-amber-600 font-medium flex items-center gap-1">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-amber-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Initializing
                </span>
              ) : (
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    isStale || tracker.status === "failed"
                      ? "bg-red-100 text-red-800"
                      : tracker.status === "completed"
                        ? "bg-green-100 text-green-800"
                        : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {isStale ? "Failed" : tracker.status.replace("_", " ")}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex gap-2 justify-end">
          {isActive ? (
            <button
              onClick={async (e) =>
                await handleAbort(
                  tracker.tracker_id,
                  e,
                  deasyApiKey,
                  setProgressTrackers,
                  setAbortingTracker,
                )
              }
              className="text-red-500 hover:text-red-700 bg-red-50 px-2 py-1 rounded-md"
            >
              {abortingTracker ? (
                <div className="flex items-center gap-2">
                  <span>Aborting</span>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                </div>
              ) : (
                "Abort"
              )}
            </button>
          ) : (
            <>
              {/* Retry button for completed, failed or stale trackers that are classify tasks */}

              <button
                onClick={async (e) => {
                  setDeletingTracker(true);
                  await handleDelete([tracker.tracker_id], e);
                  setDeletingTracker(false);
                }}
                className="text-red-500 hover:text-red-700 px-2 py-1 rounded-md"
              >
                {deletingTracker ? (
                  <div className="flex items-center gap-2">
                    <span>Deleting</span>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                  </div>
                ) : (
                  <FaTrash />
                )}
              </button>
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan="6" className="px-6 py-4 space-y-6">
            {/* Enhanced Classification Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Tag Hierarchy Visualization */}
              {tracker.task_type !== "auto_create" && (
                <HierarchyCell
                  tracker={tracker}
                  hierarchy={tracker?.hierarchy}
                />
              )}

              {/* Tag Details Panel */}
              <TagsCell tracker={tracker} />

              {/* Advanced Settings */}
              {tracker.task_type === "auto_create" && (
                <AdvancedSettingsCell
                  tracker={tracker}
                  isOpen={isAdvancedSettingsOpen}
                  setIsOpen={setIsAdvancedSettingsOpen}
                />
              )}
            </div>

            {/* Chunk Mapping - Full Width */}
            {tracker.task_type === "standardize" && (
              <ChunkMappingCell tracker={tracker} />
            )}

            {/* Classification Details */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-left">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {tracker?.llm_vendor && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FiCloud className="text-primary" size={12} />
                      <span className="text-sm font-medium text-gray-500">
                        LLM Vendor
                      </span>
                    </div>
                    <p className="mt-1 text-gray-700 font-medium">
                      {tracker.llm_vendor}
                    </p>
                  </div>
                )}

                {tracker?.llm_model && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FiCpu className="text-primary" size={12} />
                      <span className="text-sm font-medium text-gray-500">
                        LLM Model
                      </span>
                    </div>
                    <p className="mt-1 text-gray-700 font-medium">
                      {tracker.llm_model}
                    </p>
                  </div>
                )}

                {tracker?.vdb_data_source && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FiDatabase className="text-primary" size={12} />
                      <span className="text-sm font-medium text-gray-500">
                        Data Source
                      </span>
                    </div>
                    <p className="mt-1 text-gray-700 font-medium">
                      {tracker.vdb_data_source}
                    </p>
                  </div>
                )}

                {renderOnlyDedupeCell(tracker)}
                {renderTagDetailsCell(tracker)}
                {renderProcessingTimeCell(tracker)}

                {renderErrorRateCell(tracker)}
                {renderSuccessRateCell(tracker)}
                {renderSchemaNameCell(tracker)}
              </div>
            </div>

            {tracker?.files && (
              <div className="p-4 bg-white rounded-lg shadow-md border border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-800">
                    Files
                  </p>
                  <input
                    type="text"
                    placeholder="Search files..."
                    className="px-3 py-1 border rounded-md text-sm w-48"
                    value={filesSearchQuery}
                    onChange={(e) => {
                      setFilesSearchQuery(e.target.value);
                      setFilesPage(1);
                    }}
                  />
                </div>
                <div className="space-y-4">
                  {/* Paginated Files */}
                  <div className="space-y-2">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            File Name
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tracker?.files
                          .filter((file) =>
                            file
                              .toLowerCase()
                              .includes(filesSearchQuery.toLowerCase()),
                          )
                          .slice(
                            (filesPage - 1) * filesPerPage,
                            filesPage * filesPerPage,
                          )
                          .map((file, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 truncate max-w-[200px] text-left">
                                {file}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setFilesPage((p) => Math.max(1, p - 1))}
                        disabled={filesPage === 1}
                        className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {filesPage} of{" "}
                        {Math.ceil(
                          tracker.files.filter((file) =>
                            file
                              .toLowerCase()
                              .includes(filesSearchQuery.toLowerCase()),
                          ).length / filesPerPage,
                        )}
                      </span>
                      <button
                        onClick={() => setFilesPage((p) => p + 1)}
                        disabled={
                          filesPage * filesPerPage >=
                          tracker.files.filter((file) =>
                            file
                              .toLowerCase()
                              .includes(filesSearchQuery.toLowerCase()),
                          ).length
                        }
                        className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                    <select
                      value={filesPerPage}
                      onChange={(e) => {
                        setFilesPerPage(Number(e.target.value));
                        setFilesPage(1);
                      }}
                      className="p-1 text-sm border rounded-md"
                    >
                      <option value={10}>10 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Timeline section */}
            {renderTimelineCell(tracker)}

            {/* Error Message section - only show for failed status */}
            {renderErrorDataTableCell(tracker)}
          </td>
        </tr>
      )}
    </>
  );
};
